import { Box, Divider, Grid, Typography } from "@mui/material";
import { VehicleExtraImagePositions } from "@repo/types";
import { t } from "i18next";
import { CompressedImage } from "@/components/common/CompressedImage";
import type { DamagedCarImages, Vehicle } from "@/types/models/vehicle.types";
import { formatLongDateTime } from "@/utils/date";
import { humanizeCamelCase } from "@/utils/format";
import { getBeforeAfterImages, type ImageSource } from "../utils/imageUtils";

type ImageComparisonViewProps = {
  vehicle: Vehicle;
  selectedKey: keyof DamagedCarImages;
  onImageZoom: (imageSrc: string) => void;
};

type ImageMeta = {
  takenAt?: string;
  location?: {
    latitude: number;
    longitude: number;
    fullAddress?: string;
  };
  format?: string;
};

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: "4px",
        borderBottom: "0.5px solid rgba(0,0,0,0.08)",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "11px",
          color: "rgba(0,0,0,0.45)",
          letterSpacing: "0.03em",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "11px",
          fontWeight: 600,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ImageDisplay({
  title,
  image,
  altText,
  badge,
  meta,
  onImageZoom,
}: {
  title: string;
  image?: ImageSource;
  altText: string;
  badge: "before" | "after";
  meta?: ImageMeta;
  onImageZoom: (imageSrc: string) => void;
}) {
  const takenAt = formatLongDateTime(meta?.takenAt ?? "");
  const address = meta?.location?.fullAddress ?? "-";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        borderRadius: "12px",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        background: "#dcdcdc",
        boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "16px",
          py: "11px",
          background: "#fafafa",
        }}
      >
        <Typography
          sx={{
            fontSize: "13.5px",
            fontWeight: 600,
            color: "#1e293b",
            letterSpacing: "-0.01em",
          }}
        >
          {humanizeCamelCase(title)}
        </Typography>

        <Box
          component="span"
          sx={{
            fontSize: "9px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "white",
            bgcolor: "primary.main",
            px: "9px",
            py: "3px",
            borderRadius: "6px",
          }}
        >
          {badge === "before" ? t("delivery.titles.before") : t("delivery.titles.after")}
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: "#e5e5e5",
          cursor: "pointer",
          "& img": {
            transition: "transform 0.5s cubic-bezier(0.4,0,0.2,1)",
          },
          "& .info-cloud": {
            opacity: 0,
            transform: "translateY(6px)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
          },
          "&:hover img": {
            transform: "scale(1.04)",
          },
          "&:hover .info-cloud": {
            opacity: 1,
            transform: "translateY(0)",
          },
        }}
      >
        {image ? (
          <CompressedImage
            image={image}
            alt={altText}
            onClick={() => onImageZoom(image.url)}
            sx={{ display: "block", width: "100%", height: "300px", objectFit: "cover" }}
          />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ height: "300px", color: "rgba(0,0,0,0.25)" }}
          >
            <Typography sx={{ fontSize: "12px", fontFamily: "'DM Mono', monospace" }}>
              No image available
            </Typography>
          </Box>
        )}

        {image && meta && (
          <Box
            className="info-cloud"
            onClick={() => onImageZoom(image.url)}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 3,
              backdropFilter: "blur(1px)",
              background: "rgba(255,255,255,0.75)",
              borderTop: "0.5px solid rgba(255,255,255,0.6)",
              px: "16px",
              pt: "12px",
              pb: "10px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: "2px",
            }}
          >
            {meta.takenAt && <MetaRow label="Captured" value={takenAt} />}
            {meta.location && <MetaRow label="Location" value={address} />}
            {meta.format && <MetaRow label="Format" value={meta.format.toUpperCase()} />}
          </Box>
        )}
      </Box>
    </Box>
  );
}

const EXTRA_IMAGE_KEYS = new Set<string>(VehicleExtraImagePositions);
function getImageMeta(images: any, key: string): ImageMeta {
  if (!images) return {};
  const value = EXTRA_IMAGE_KEYS.has(key) ? images.extraImages?.[key] : images[key];
  if (!value || typeof value !== "object") {
    const metadataKey = `${key}Metadata`;
    return images[metadataKey] ?? images.extraImages?.[metadataKey] ?? {};
  }
  const { takenAt, location, format } = value as Record<string, unknown>;
  return {
    takenAt: typeof takenAt === "string" ? takenAt : undefined,
    location: location as ImageMeta["location"] | undefined,
    format: typeof format === "string" ? format : undefined,
  };
}

export function ImageComparisonView({
  vehicle,
  selectedKey,
  onImageZoom,
}: ImageComparisonViewProps) {
  const { beforeImage, afterImage } = getBeforeAfterImages(vehicle, selectedKey);
  const beforeMeta = getImageMeta(vehicle?.imagesBefore, selectedKey);
  const afterMeta = getImageMeta(vehicle?.imagesAfter, selectedKey);
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <ImageDisplay
          title={String(selectedKey)}
          image={beforeImage}
          altText={`Before - ${selectedKey}`}
          badge="before"
          meta={beforeMeta}
          onImageZoom={onImageZoom}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <ImageDisplay
          title={String(selectedKey)}
          image={afterImage}
          altText={`After - ${selectedKey}`}
          badge="after"
          meta={afterMeta}
          onImageZoom={onImageZoom}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider sx={{ mt: 1, mb: 1 }} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography variant="body2" align="center" color="text.secondary">
          Hover image to inspect · click to zoom
        </Typography>
      </Grid>
    </Grid>
  );
}
